import { useEffect, useState } from "react";
import { Download, RotateCcw, Loader2, Eraser, ListOrdered } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { api } from "../../lib/api";
import { makePoller } from "../../lib/monitor";
import { useWorkspace } from "../../store/WorkspaceContext";
import { useAuth } from "../../store/AuthContext";
import { toLocal, fmtIn } from "../../engine/geometry";
import { DEFAULT_CONNECTION } from "../../lib/transport";

// What is waiting to reach the plotter right now, with a one-click clear (cloud queue + Windows spooler).
const QueuePanel = ({ state }) => {
  const c = { ...DEFAULT_CONNECTION, ...state.connection };
  const [q, setQ] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.relayQueue(c.shopCode).then(setQ).catch(() => setQ({ pending: 0, jobs: [] }));
  useEffect(() => makePoller(() => api.relayQueue(c.shopCode).then(setQ), { base: 4000, max: 30000 }), [c.shopCode]); // eslint-disable-line react-hooks/exhaustive-deps
  const clear = async () => {
    setBusy(true);
    try {
      const r = await api.relayQueueClear(c.shopCode, { type: c.targetType, printerName: c.printerName, serialPort: c.serialPort });
      toast.success(`Plotter queue cleared — ${r.canceled} waiting job${r.canceled === 1 ? "" : "s"} cancelled${r.connector_online ? ", print spooler purged" : " (shop computer offline: spooler will be purged when it reconnects)"}. Select a template and press Cut to send the next pattern.`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not clear the queue"); } finally { setBusy(false); }
  };
  const live = Boolean(state.bridge?.ok);
  return (
    <div className="rounded-md border border-[#27324a] bg-[#0c1017] p-3" data-testid="plotter-queue">
      <div className="flex items-center gap-2">
        <ListOrdered className="h-4 w-4 text-[#4c9ffe]" />
        <div className="flex-1">
          <div className="text-[13px] font-semibold">Plotter queue</div>
          <div className="text-[11px] text-[#9aa0a8]" data-testid="plotter-queue-status">{q === null ? "Checking…" : q.pending ? `${q.pending} job${q.pending === 1 ? "" : "s"} waiting to reach the plotter` : "Nothing waiting — the plotter will only cut what you select next"}{live ? "" : " · shop computer offline"}</div>
        </div>
        <button data-testid="clear-queue-btn" className="btn-red" disabled={busy} onClick={clear} title="Cancel waiting jobs and purge the plotter's print spooler"><Eraser className="h-3.5 w-3.5" /> Clear plotter queue</button>
      </div>
      <p className="mt-2 text-[10px] text-[#6b7079]">Use this after a plotter reset or blade change so an old pattern can't re-cut. Waiting jobs also expire on their own after 2 minutes, and every new Cut replaces anything still waiting.</p>
    </div>
  );
};

export const JobHistoryDialog = () => {
  const { state, dispatch } = useWorkspace();
  const isAdmin = useAuth()?.user?.role === "admin";
  const open = state.dialog === "jobs";
  const [jobs, setJobs] = useState(null);
  const close = () => dispatch({ type: "SET", patch: { dialog: null } });
  useEffect(() => { if (open) { setJobs(null); api.jobs().then(setJobs).catch(() => toast.error("Failed to load jobs")); } }, [open]);

  const restore = async (j) => {
    try {
      const vehicle = j.vehicle_id ? await api.vehicles().then((vs) => vs.find((v) => v.id === j.vehicle_id)) : null;
      const shadowed = new Set(j.pieces.filter((p) => p.shadow).map((p) => p.pattern_id));
      const pieces = j.pieces.filter((p) => !p.shadow).map((p, i) => {
        const base = { x: p.x, y: p.y, rotation: p.rotation };
        const points = p.world_points.map((w) => toLocal(base, w));
        return { id: p.pattern_id, patternId: p.pattern_id, name: p.name, pieceType: p.name.toLowerCase().includes("quarter") ? "quarter" : p.name.toLowerCase().includes("back") ? "back_glass" : p.name.toLowerCase().includes("rear") ? "rear_door" : "front_door", side: "", version: p.version, points, basePoints: points, ...base, included: true, appliedOffset: 0, dirty: false, publisherName: "Job snapshot", publisherTier: 1, shadow: shadowed.has(p.pattern_id) };
      });
      dispatch({ type: "SET", patch: { vehicle: vehicle || { id: j.vehicle_id, make: j.vehicle_name, model: "", year_start: "", year_end: "", body_style: "restored job" }, rollWidth: j.roll_width, spacing: j.spacing, margin: j.margin, locked: false } });
      dispatch({ type: "LOAD_PIECES", pieces, nestResult: { rollLength: j.roll_length_in, utilization: j.utilization, areaUsed: j.area_used_sqin, overflow: [], placements: [], ms: 0 } });
      toast.success(`Restored layout from job "${j.name}"`);
      close();
    } catch { toast.error("Could not restore job layout"); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="panel3d max-w-2xl border-[#27324a] bg-[#121824] text-[#e8e9ec]" data-testid="job-history-dialog">
        <DialogHeader>
          <DialogTitle className="text-[15px]">Job history</DialogTitle>
          <DialogDescription className="text-[#9aa0a8]">Every cut is recorded with its layout, pattern versions and plotter profile.</DialogDescription>
        </DialogHeader>
        <QueuePanel state={state} />
        {!jobs ? <div className="flex items-center gap-2 text-xs text-[#9aa0a8]"><Loader2 className="h-3 w-3 animate-spin" />Loading…</div> : !jobs.length ? <div className="text-xs text-[#6b7079]" data-testid="no-jobs">No jobs yet. Send a cut job to see it here.</div> : (
          <ul className="max-h-[360px] space-y-1.5 overflow-y-auto" data-testid="job-list">
            {jobs.map((j) => (
              <li key={j.id} data-testid={`job-row-${j.id}`} className="rounded-sm border border-[#27324a] bg-[#1a2231] p-2.5">
                <div className="flex items-center gap-2">
                  <span className={`rounded-sm px-1.5 py-0.5 font-mono text-[9px] font-bold uppercase ${j.status === "sent" ? "bg-[#3ecf8e]/20 text-[#3ecf8e]" : "bg-[#f5a524]/20 text-[#f5a524]"}`}>{j.status}</span>
                  <span className="flex-1 truncate text-sm">{j.name}</span>
                  <span className="font-mono text-[10px] text-[#6b7079]">{new Date(j.created_at).toLocaleString()}</span>
                </div>
                <div className="mt-1 flex flex-wrap gap-x-3 font-mono text-[10px] text-[#9aa0a8]">
                  <span>{j.machine_name}</span><span>{j.command_format.toUpperCase()} · {j.command_count} cmds</span><span>{j.pieces.length} pcs</span>
                  <span>{j.roll_width}" × {fmtIn(j.roll_length_in)}</span><span>{(j.utilization * 100).toFixed(1)}% eff.</span>
                  <span>versions: {[...new Set(j.pieces.map((p) => `v${p.version}`))].join(", ")}</span>
                </div>
                <div className="mt-1.5 flex gap-1.5">
                  {isAdmin && <a data-testid={`job-download-${j.id}`} href={api.commandsUrl(j.id)} className="btn"><Download className="h-3.5 w-3.5" /> Command file</a>}
                  <button data-testid={`job-restore-${j.id}`} onClick={() => restore(j)} className="btn"><RotateCcw className="h-3.5 w-3.5" /> Restore layout</button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
};
